import { useCallback, useMemo, useState } from 'react';

import { IngredientAssetVersion, type Vendor } from '@sg/graphql-schema';
import {
  type CustomizationMachineTypes,
  findFirstUnmetGroupLimits,
  useCustomizationContext,
  useCustomizationHelpers,
  useInitCustomizationMachine,
  useRetryAttempts,
} from '@sg/shared-fe-features-customization';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  useCustomizationExternalActions,
  useCustomizeCartMutations,
} from '../../../CustomizationScreen/components/Customization/hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Runs the customization machine without rendering the customization UI.
 *
 * The machine completes on its own as soon as it is ready, unless the product
 * has out-of-stock ingredients. In that case the caller decides whether to add
 * the product without them (`addToBag`) or to open the full customization.
 */
export const useQuickAddCustomization = (params: Params) => {
  const {
    productSlug,
    restaurantSlug,
    sharedLineItemId,
    addressId,
    deliveryFee,
    vendor,
    onAddToBagSuccess,
    onCompletionBlocked,
  } = params;

  const isUpsellPricingEnabled = useFeatureFlag('CELS-5026-customization-upsell-pricing-enabled');

  // ─── State ───────────────────────────────────────────────────────────

  const [hasFailedToAddToBag, setHasFailedToAddToBag] = useState(false);
  const [hasRequestedAddToBag, setHasRequestedAddToBag] = useState(false);

  // ─── Cart ────────────────────────────────────────────────────────────

  const handleAddToBagError = useCallback(() => {
    setHasRequestedAddToBag(false);
    setHasFailedToAddToBag(true);
  }, []);

  const { modifyCart, isAddingLineItemToCart } = useCustomizeCartMutations({
    addressId,
    deliveryFee,
    vendor,
    onAddLineItemToCart: onAddToBagSuccess,
    onAddLineItemToCartError: handleAddToBagError,
  });

  const handleCustomizationComplete = useCallback(
    (params: Parameters<typeof modifyCart>[0]) => {
      setHasRequestedAddToBag(true);

      return modifyCart(params);
    },
    [modifyCart],
  );

  const externalActions = useCustomizationExternalActions({
    handleCustomizationComplete,
  });

  const handleMaxModificationsExceeded = useCallback<CustomizationMachineAction>(
    (...args) => {
      externalActions.onMaxModificationsExceeded?.(...args);
      onCompletionBlocked();
    },
    [externalActions, onCompletionBlocked],
  );

  const handleMinUnmet = useCallback<CustomizationMachineAction>(
    (...args) => {
      externalActions.onMinUnmet?.(...args);
      onCompletionBlocked();
    },
    [externalActions, onCompletionBlocked],
  );

  // ─── Machine ─────────────────────────────────────────────────────────

  const actor = useInitCustomizationMachine(
    {
      productSlug,
      restaurantSlug,
      lineItemId: undefined,
      sharedLineItemId,
      ingredientAssetVersion: IngredientAssetVersion.V2,
      isDelivery: Boolean(addressId),
      isUpsellPricingEnabled,
    },
    {
      actions: {
        ...externalActions,
        onMaxModificationsExceeded: handleMaxModificationsExceeded,
        onMinUnmet: handleMinUnmet,
      },
      guards: {
        checkIfShouldAutoComplete,
      },
    },
  );

  const {
    isFetching,
    hasFailedToFetch,
    isCustomizationActive,
    product,
    outOfStockIngredients,
    activeModifications,
    modificationLimits,
  } = useCustomizationContext({ actor });

  const { completeCustomization, retryFetchCustomizationData } = useCustomizationHelpers({ actor });

  const { hasReachedAttemptLimit: hasReachedFetchAttemptLimit, retry: retryFetch } =
    useRetryAttempts({ onRetry: retryFetchCustomizationData });

  // ─── Derived ─────────────────────────────────────────────────────────

  const isAddingToBag = isAddingLineItemToCart || hasRequestedAddToBag;

  const hasOutOfStockIngredients = outOfStockIngredients.length > 0;
  const shouldShowUnavailableIngredients = isCustomizationActive && hasOutOfStockIngredients;

  const isBlocking = useMemo(() => {
    const firstUnmetGroupLimits = findFirstUnmetGroupLimits(
      activeModifications,
      modificationLimits,
    );

    return Boolean(firstUnmetGroupLimits);
  }, [activeModifications, modificationLimits]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetching,
    hasFailedToFetch,
    hasFailedToAddToBag,
    hasReachedFetchAttemptLimit,
    product,
    outOfStockIngredients,
    isBlocking,
    shouldShowUnavailableIngredients,
    isAddingToBag,
    retryFetch,
    addToBag: completeCustomization,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfShouldAutoComplete() {
  return true;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  productSlug: string;
  restaurantSlug: string;
  sharedLineItemId?: string;
  addressId?: string;
  deliveryFee?: number;
  vendor?: Vendor;
  onAddToBagSuccess: () => void;
  onCompletionBlocked: () => void;
};

type CustomizationMachineAction = CustomizationMachineTypes.CustomizationMachineAction;
